import { AgGridReact } from 'ag-grid-react';
import { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from "react-router-dom";
import SidebarLayout from '../../../../layouts/SidebarLayout';
import sidebar from '../../utils/sidebar';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import IconButton from '../../../../components/Buttons/IconButton';
import { IoCreateOutline } from 'react-icons/io5';
import { BsTrash3 } from 'react-icons/bs';
import { del, get } from '../../../../utils/helpers/api';
import { toast } from 'react-toastify';
import DefaultPagination from '../../../../components/Pagination';
import ConfirmationModal from '../../../../components/Modals/Confirmation';
import ViewDrawer from './Drawers/View';
import PageSubHeader from "../../../../components/Layouts/PageSubHeader";
import DefaultButton from "../../../../components/Buttons/Default";
import { Badge } from 'reactstrap';
import { TfiFilter } from 'react-icons/tfi';
import FiltersDrawer from './Drawers/Filters';
import { printQuoteStatus } from '../../utils/helpers';
import { useTranslation } from 'react-i18next';
import { useBranch } from "../../../../context/branch";
import i18n from '../../../../i18n';
import { useSettings } from '../../../../context/settings';
import AgGridTable from '../../../../components/Tables/AgGrid';

ModuleRegistry.registerModules([AllCommunityModule]);

function ActionsCellRenderer({ data, context }) {
  return (
    <div className="d-flex align-items-center gap-2 h-100">
      <IconButton
        onClick={(e) => {
          e.stopPropagation();
          context.navigate(`/sales/quotes/edit/${data.id}`, { state: { quoteData: data } });
        }}
        size="sm"
        outline
        color="secondary"
      >
        <IoCreateOutline />
      </IconButton>

      <IconButton
        onClick={(e) => {
          e.stopPropagation();
          context.setQuoteToDelete(data.id);
        }}
        size="sm"
        outline
        color="danger"
      >
        <BsTrash3 />
      </IconButton>
    </div>
  );
}

function QuotesPage() {
  const { t } = useTranslation();
  const { isMultiBranch, allBranchesMode } = useBranch();
  const { formatDate } = useSettings();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rowData, setRowData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [quoteToDelete, setQuoteToDelete] = useState(null);
  const [selectedQuoteId, setSelectedQuoteId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const FILTER_KEYS = ['min_amount', 'max_amount', 'status', 'added_after', 'added_before'];

  const [filters, setFilters] = useState(() => {
    const initial = {};
    FILTER_KEYS.forEach(key => {
      const val = searchParams.get(key);
      if (val) initial[key] = val;
    });
    return initial;
  });

  const filtersCount = Object.values(filters).filter(v => v).length;

  const getQuotes = useCallback(async () => {
    setLoading(true);
    let query = '/sales/quotes?page=' + currentPage + '&page_size=' + pageSize;
    const activeFilters = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    Object.entries(activeFilters).forEach(([key, value]) => {
      query += `&${key}=${encodeURIComponent(value)}`;
    });
    const data = await get(query);
    if (data?.quotes?.data) {
      setRowData(data.quotes.data);
      setTotalPages(data.quotes.last_page);
      setTotalRecords(data.quotes.total);
    }
    setLoading(false);
  }, [currentPage, pageSize, filters]);

  useEffect(() => {
    getQuotes();
  }, [getQuotes]);

  const deleteQuote = async () => {
    const data = await del('/sales/quotes/delete/' + quoteToDelete);

    if (data?.success) {
      toast.success(data.message || t('sales.quoteDeleted'));
      getQuotes();
    } else {
      toast.error(data.message || t('common.somethingWentWrong'));
    }
    setQuoteToDelete(null);
  };

  const columnDefs = useMemo(() => [
    // Only worth a column when there is more than one branch to tell apart.
    ...(isMultiBranch || allBranchesMode ? [{
      field: 'branch.code',
      headerName: t('branches.branch'),
      minWidth: 110,
      sortable: true,
      valueGetter: (p) => p.data?.branch?.code ?? '-'
    }] : []),
    { field: 'quote_number', headerName: '#', width: 80, sortable: true },
    {
      field: 'customer',
      headerName: t('sales.client'),
      flex: 1,
      minWidth: 150,
      sortable: true,
      valueFormatter: (params) => {
        if(params?.data?.quote_for === 'lead') {
          const leadName = params?.data?.lead?.name;
          if (leadName) return leadName;
          const firstName = params?.data?.lead?.firstname || '';
          const lastName = params?.data?.lead?.lastname || '';
          return firstName + ' ' + lastName;
        }
        const companyName = params?.data?.customer?.company_name;
        if (companyName) return companyName;
        const firstName = params?.data?.customer?.firstname || '';
        const lastName = params?.data?.customer?.lastname || '';
        return firstName + ' ' + lastName;
      }
    },
    {
      field: 'total_amount',
      headerName: t('common.amount'),
      sortable: true,
      width: 140,
      valueFormatter: (params) => {
        return params?.data?.total_amount + ' ' + params?.data?.currency;
      }
    },
    {
      field: 'quote_date',
      headerName: t('common.date'),
      width: 150,
      sortable: true,
      valueFormatter: (params) => formatDate(params?.data?.quote_date)
    },
    {
      field: 'status',
      headerName: t('common.status'),
      flex: 1,
      minWidth: 140,
      sortable: true,
      cellRenderer: (params) => {
        return (
          <div className="d-flex align-items-center gap-1">
            { printQuoteStatus(params?.data?.status) }
            { params?.data?.rejection_reason && <Badge color="secondary">{ params?.data?.rejection_reason }</Badge> }
          </div>
        );
      }
    },
    {
      headerName: i18n.t('common.options'),
      width: 110,
      sortable: false,
      resizable: false,
      cellRenderer: ActionsCellRenderer,
    }
  ], [t, formatDate, isMultiBranch, allBranchesMode]);

  const defaultColDef = useMemo(() => ({
    resizable: true,
  }), []);

  const gridContext = useMemo(() => ({ navigate, setQuoteToDelete }), [navigate]);

  const onRowClicked = (event) => {
    if (event.event?.target?.closest('button')) return;
    setSelectedQuoteId(event.data.id);
    setDrawerOpen(true);
  };

  const handleApplyFilters = (filtersData) => {
    const activeFilters = Object.fromEntries(Object.entries(filtersData).filter(([, v]) => v));
    setFilters(activeFilters);
    setSearchParams(activeFilters);
    setCurrentPage(1);
    setFiltersOpen(false);
  }

  return(
    <SidebarLayout sidebar={sidebar}>
      <div className="page-with-subheader">
        <PageSubHeader title={t('sales.quotes.title')} onReload={getQuotes} dataTotal={totalRecords}>
          <button className="subheader-btn" type="button" onClick={() => setFiltersOpen(true)}>
            <TfiFilter size={13} />
            {t('common.filter')}
            {filtersCount > 0 && (
              <Badge color="primary" className="filter-badge">{filtersCount}</Badge>
            )}
          </button>
          <div className="subheader-divider" />
          <DefaultButton color="primary" size="sm" onClick={() => navigate('/sales/quotes/create')}>
            {t('sales.newQuote')}
          </DefaultButton>
        </PageSubHeader>
        <div className="subheader-page-content">
          <AgGridTable 
            rowData={rowData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            loading={loading}
            context={gridContext}
            onRowClicked={onRowClicked}
          />

          <DefaultPagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </div>
      </div>

      <FiltersDrawer
        isOpen={filtersOpen}
        toggle={() => setFiltersOpen(false)}
        filters={filters}
        applyFilters={handleApplyFilters}
      />

      <ConfirmationModal
        isOpen={quoteToDelete !== null}
        title={t('sales.deleteQuote')}
        message={t('sales.deleteQuoteConfirm')}
        onConfirm={deleteQuote}
        toggle={() => setQuoteToDelete(null)}
      />

      <ViewDrawer
        quoteId={selectedQuoteId}
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onDelete={() => {
          setDrawerOpen(false);
          setQuoteToDelete(selectedQuoteId);
        }}
        refreshQuotes={getQuotes}
      />
    </SidebarLayout>
  );
}

export default QuotesPage;
